import { Grid, Skeleton, Stack } from '@mui/material';
import React from 'react';
import Header from '../header/Header';

const SkeletonRadioRow: React.FC = () => (
  <Stack direction="row" gap={1} alignItems="center">
    <Skeleton variant="circular" width={25} height={25} />
    <Skeleton
      variant="rectangular"
      width="20%"
      height={15}
      sx={{ borderRadius: '0.2rem' }}
    />
  </Stack>
);

interface SkeletonCardProps {
  radioRowCount: number;
  showButtonRow?: boolean;
}

const SkeletonCard: React.FC<SkeletonCardProps> = ({
  radioRowCount,
  showButtonRow,
}) => {
  const radioRowKeys = Array.from(
    { length: radioRowCount },
    (_, rowNumber) => `skeleton-radio-row-${radioRowCount}-${rowNumber + 1}`,
  );

  return (
    <Stack p={3} width="100%" className="card" gap={3}>
      <Skeleton
        variant="rectangular"
        width="70%"
        height={30}
        sx={{ borderRadius: '0.5rem' }}
      />
      {radioRowKeys.map((rowKey) => (
        <SkeletonRadioRow key={rowKey} />
      ))}
      {showButtonRow && (
        <Stack direction="row" gap={1} alignItems="center">
          <Skeleton
            variant="rectangular"
            width="100%"
            height={45}
            sx={{ borderRadius: '0.5rem' }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={45}
            sx={{ borderRadius: '0.5rem' }}
          />
        </Stack>
      )}
    </Stack>
  );
};

const ContentSkeleton: React.FC = () => {
  return (
    <Stack
      justifyContent="space-between"
      height="100vh"
      data-testid="skeleton-loader"
    >
      {/* Header */}
      <Header />

      {/* Middle Content */}
      <Grid item xs={12} p={4}>
        <Stack
          width="100%"
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={4}
        >
          <SkeletonCard radioRowCount={4} showButtonRow />
          <SkeletonCard radioRowCount={5} />
        </Stack>
      </Grid>

      {/* Footer */}
      <footer>
        <Stack
          direction="row"
          gap={1}
          justifyContent="center"
          alignItems="center"
        >
          <a id="jetstream-doc" href="/docs/JetstreamHelp.doc" download>
            <Stack
              direction="row"
              gap={1}
              justifyContent="center"
              alignItems="center"
            >
              <svg
                aria-hidden="true"
                focusable="false"
                version="1.1"
                viewBox="0 0 32 32"
              >
                <path d="M27.844 11.252c-0.101-4.022-3.389-7.252-7.433-7.252-2.369 0-4.477 1.109-5.839 2.835-0.764-0.987-1.959-1.624-3.303-1.624-2.307 0-4.176 1.871-4.176 4.179 0 0.201 0.015 0.399 0.043 0.592-0.351-0.063-0.711-0.098-1.080-0.098-3.344-0-6.054 2.712-6.054 6.058s2.71 6.058 6.054 6.058h2.868l7.078 7.328 7.078-7.328 3.484-0c3.004-0.006 5.438-2.444 5.438-5.451 0-2.565-1.771-4.716-4.156-5.296zM16 26l-6-6h4v-6h4v6h4l-6 6z"></path>
              </svg>
              JetStream Help
            </Stack>
          </a>
        </Stack>

        {/* Button Skeleton */}
        <Skeleton
          variant="rectangular"
          width={162}
          height={48}
          sx={{ borderRadius: '0.5rem' }}
        />
      </footer>
    </Stack>
  );
};

export default ContentSkeleton;
